import { Link } from "react-router-dom";
import { STATUS_STYLES } from "@/lib/constants";
import { todayIso as localTodayIso } from "@/lib/dates";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOTS = {
  pending: "bg-amber-400",
  assigned: "bg-blue-500",
  awaiting_inspection: "bg-violet-500",
  closed: "bg-slate-400",
  scheduled: "bg-blue-500",
  in_progress: "bg-indigo-500",
  completed: "bg-emerald-500",
};

export default function MonthCalendar({ jobs, month, onMonthChange }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const todayIso = localTodayIso();

  const iso = (day) =>
    `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const byDate = jobs.reduce((acc, j) => {
    (acc[j.scheduled_date] = acc[j.scheduled_date] || []).push(j);
    return acc;
  }, {});

  const shift = (n) => onMonthChange(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="month-calendar">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">
          {month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </h2>
        <div className="flex items-center gap-2">
          <button
            data-testid="calendar-prev-month"
            onClick={() => shift(-1)}
            className="h-11 w-11 md:h-8 md:w-8 rounded-md border border-border flex items-center justify-center text-royal hover:bg-royal/5 active:bg-royal/10 touch-manipulation select-none transition-colors duration-200"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-5 w-5 md:h-4 md:w-4" />
          </button>
          <button
            data-testid="calendar-today"
            onClick={() => onMonthChange(new Date())}
            className="h-11 md:h-8 rounded-md border border-border px-4 md:px-3 text-xs font-medium text-royal hover:bg-royal/5 active:bg-royal/10 touch-manipulation select-none transition-colors duration-200"
          >
            Today
          </button>
          <button
            data-testid="calendar-next-month"
            onClick={() => shift(1)}
            className="h-11 w-11 md:h-8 md:w-8 rounded-md border border-border flex items-center justify-center text-royal hover:bg-royal/5 active:bg-royal/10 touch-manipulation select-none transition-colors duration-200"
            aria-label="Next month"
          >
            <ChevronRight className="h-5 w-5 md:h-4 md:w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-border bg-secondary/60">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const date = day ? iso(day) : null;
          const dayJobs = date ? byDate[date] || [] : [];
          return (
            <div
              key={date || `pad-${i}`}
              data-testid={date ? `calendar-day-${date}` : undefined}
              className={`min-h-[124px] md:min-h-[112px] border-b border-r border-border p-2 ${
                date === todayIso ? "bg-gold/10" : day ? "bg-white" : "bg-secondary/30"
              }`}
            >
              {day && (
                <>
                  <p className={`text-xs font-semibold ${date === todayIso ? "text-royal-deep" : "text-muted-foreground"}`}>
                    {day}
                  </p>
                  <div className="mt-1.5 space-y-1">
                    {dayJobs.slice(0, 3).map((j) => (
                      <Link
                        key={j._id}
                        to={`/jobs/${j._id}`}
                        data-testid={`calendar-job-${j._id}`}
                        className={`flex items-center justify-center sm:justify-start gap-1 sm:gap-1.5 rounded px-1 sm:px-1.5 py-1.5 min-h-[32px] md:min-h-0 md:py-1 text-[11px] border touch-manipulation hover:-translate-y-[1px] transition-transform duration-200 ${
                          STATUS_STYLES[j.status] || "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                      >
                        <span className={`h-2 w-2 sm:h-1.5 sm:w-1.5 rounded-full shrink-0 ${DOTS[j.status] || "bg-slate-400"}`} />
                        <span className="truncate font-medium hidden sm:inline">{j.scheduled_time} {j.property_name}</span>
                        <span className="sr-only sm:hidden">{j.scheduled_time} {j.property_name}</span>
                      </Link>
                    ))}
                    {dayJobs.length > 3 && (
                      <p className="text-[10px] text-muted-foreground pl-1">+{dayJobs.length - 3} more</p>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
